"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PopulatableEthersLiquity = exports.PopulatedEthersRedemption = exports.PopulatedEthersLiquityTransaction = exports.SentEthersLiquityTransaction = exports.EthersTransactionCancelledError = exports._RawErrorReason = exports._redeemMaxIterations = void 0;
const assert_1 = __importDefault(require("assert"));
const bignumber_1 = require("@ethersproject/bignumber");
const constants_1 = require("@ethersproject/constants");
const logger_1 = require("@ethersproject/logger");
const lib_base_1 = require("@liquity/lib-base");
const EthersLiquityConnection_1 = require("./EthersLiquityConnection");
const _utils_1 = require("./_utils");
const contracts_1 = require("./contracts");
const parseLogs_1 = require("./parseLogs");
const bigNumberMax = (a, b) => ((b === null || b === void 0 ? void 0 : b.gt(a)) ? b : a);
const bigNumberMin = (a, b) => ((b === null || b === void 0 ? void 0 : b.gt(a)) ? a : b);
// With 70 iterations redemption costs about ~10M gas, and each iteration accounts for ~138k more
/** @internal */
exports._redeemMaxIterations = 70;
const defaultBorrowingRateSlippageTolerance = lib_base_1.Decimal.from(0.005); // 0.5%
const defaultRedemptionRateSlippageTolerance = lib_base_1.Decimal.from(0.001); // 0.1%
const defaultBorrowingFeeDecayToleranceMinutes = 10;
const noDetails = () => undefined;
const compose = (f, g) => (_) => f(g(_));
const id = (t) => t;
// Takes ~6-7K (use 10K to be safe) to update lastFeeOperationTime, but the cost of calculating the
// decayed baseRate increases logarithmically with time elapsed since the last update.
const addGasForBaseRateUpdate = (maxMinutesSinceLastUpdate = 10) => (gas) => gas.add(10000 + 1414 * Math.ceil(Math.log2(maxMinutesSinceLastUpdate + 1)));
// First traversal in ascending direction takes ~50K, then ~13.5K per extra step.
// 80K should be enough for 3 steps, plus some extra to be safe.
const addGasForPotentialListTraversal = (gas) => gas.add(80000);
const addGasForYETIIssuance = (gas) => gas.add(50000);
const addGasForSPClaim = (gas) => gas.add(100000);
const addGasForUnipoolRewardUpdate = (gas) => gas.add(20000);
const gasLimit = (gas) => bignumber_1.BigNumber.from(Math.min(gas.toNumber() + 50000, 7900000));
// To get the best entropy available, we'd do something like:
//
// const bigRandomNumber = () =>
//   BigNumber.from(
//     `0x${Array.from(crypto.getRandomValues(new Uint32Array(8)))
//       .map(u32 => u32.toString(16).padStart(8, "0"))
//       .join("")}`
//   );
//
// However, Window.crypto is browser-specific. Since we only use this for randomly picking Troves
// during the search for hints, Math.random() will do fine, too.
//
// This returns a random integer between 0 and Number.MAX_SAFE_INTEGER
const randomInteger = () => Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
// Maximum number of trials to perform in a single getApproxHint() call. If the number of trials
// required to get a statistically "good" hint is larger than this, the search for the hint will
// be broken up into multiple getApproxHint() calls.
//
// This should be low enough to work with popular public Ethereum providers like Infura without
// triggering any fair use limits.
const maxNumberOfTrialsAtOnce = 2500;
function* generateTrials(totalNumberOfTrials) {
    assert_1.default(Number.isInteger(totalNumberOfTrials) && totalNumberOfTrials > 0);
    while (totalNumberOfTrials) {
        const numberOfTrials = Math.min(totalNumberOfTrials, maxNumberOfTrialsAtOnce);
        yield numberOfTrials;
        totalNumberOfTrials -= numberOfTrials;
    }
}
function decToBN(decimals) {
    const bignums = [];
    for (let i = 0; i < decimals.length; i++) {
        bignums.push(bignumber_1.BigNumber.from(decimals[i].hex));
    }
    return bignums;
}
function convertToMap(keys, values) {
    const r = {};
    for (let i = 0; i < keys.length; i++) {
        if (typeof values[i] !== typeof lib_base_1.Decimal.ZERO) {
            r[keys[i]] = lib_base_1.Decimal.from(values[i]);
        }
        else {
            r[keys[i]] = values[i];
        }
    }
    return r;
}
/** @internal */
var _RawErrorReason;
(function (_RawErrorReason) {
    _RawErrorReason["TRANSACTION_FAILED"] = "transaction failed";
    _RawErrorReason["TRANSACTION_CANCELLED"] = "cancelled";
    _RawErrorReason["TRANSACTION_REPLACED"] = "replaced";
    _RawErrorReason["TRANSACTION_REPRICED"] = "repriced";
})(_RawErrorReason = exports._RawErrorReason || (exports._RawErrorReason = {}));
const transactionReplacementReasons = [
    _RawErrorReason.TRANSACTION_CANCELLED,
    _RawErrorReason.TRANSACTION_REPLACED,
    _RawErrorReason.TRANSACTION_REPRICED
];
const hasProp = (o, p) => p in o;
const isTransactionFailedError = (error) => hasProp(error, "code") &&
    error.code === logger_1.ErrorCode.CALL_EXCEPTION &&
    hasProp(error, "reason") &&
    error.reason === _RawErrorReason.TRANSACTION_FAILED;
const isTransactionReplacedError = (error) => hasProp(error, "code") &&
    error.code === logger_1.ErrorCode.TRANSACTION_REPLACED &&
    hasProp(error, "reason") &&
    transactionReplacementReasons.includes(error.reason);
/**
 * Thrown when a transaction is cancelled or replaced by a different transaction.
 *
 * @public
 */
class EthersTransactionCancelledError extends Error {
    /** @internal */
    constructor(rawError) {
        assert_1.default(rawError.reason !== _RawErrorReason.TRANSACTION_REPRICED);
        super(`Transaction ${rawError.reason}`);
        this.name = "TransactionCancelledError";
        this.rawReplacementReceipt = rawError.receipt;
        this.rawError = rawError;
    }
}
exports.EthersTransactionCancelledError = EthersTransactionCancelledError;
/**
 * A transaction that has already been sent.
 *
 * @remarks
 * Returned by {@link SendableEthersLiquity} functions.
 *
 * @public
 */
class SentEthersLiquityTransaction {
    /** @internal */
    constructor(rawSentTransaction, connection, parse) {
        this.rawSentTransaction = rawSentTransaction;
        this._connection = connection;
        this._parse = parse;
    }
    _receiptFrom(rawReceipt) {
        // console.log()
        return rawReceipt
            ? rawReceipt.status
                ? lib_base_1._successfulReceipt(rawReceipt, this._parse(rawReceipt), () => parseLogs_1.logsToString(rawReceipt, EthersLiquityConnection_1._getContracts(this._connection)))
                : lib_base_1._failedReceipt(rawReceipt)
            : lib_base_1._pendingReceipt;
    }
    async _waitForRawReceipt(confirmations) {
        try {
            return await this.rawSentTransaction.wait(confirmations);
        }
        catch (error) {
            if (error instanceof Error) {
                if (isTransactionFailedError(error)) {
                    return error.receipt;
                }
                if (isTransactionReplacedError(error)) {
                    if (error.cancelled) {
                        throw new EthersTransactionCancelledError(error);
                    }
                    else {
                        return error.receipt;
                    }
                }
            }
            throw error;
        }
    }
    /** {@inheritDoc @liquity/lib-base#SentLiquityTransaction.getReceipt} */
    async getReceipt() {
        return this._receiptFrom(await this._waitForRawReceipt(0));
    }
    /**
     * {@inheritDoc @liquity/lib-base#SentLiquityTransaction.waitForReceipt}
     *
     * @throws
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    async waitForReceipt() {
        const receipt = this._receiptFrom(await this._waitForRawReceipt());
        assert_1.default(receipt.status !== "pending");
        return receipt;
    }
}
exports.SentEthersLiquityTransaction = SentEthersLiquityTransaction;
const normalizeBorrowingOperationOptionalParams = (maxBorrowingRateOrOptionalParams, currentBorrowingRate) => {
    var _a, _b;
    if (maxBorrowingRateOrOptionalParams === undefined) {
        return {
            maxBorrowingRate: (_a = currentBorrowingRate === null || currentBorrowingRate === void 0 ? void 0 : currentBorrowingRate.add(defaultBorrowingRateSlippageTolerance)) !== null && _a !== void 0 ? _a : lib_base_1.Decimal.ZERO,
            borrowingFeeDecayToleranceMinutes: defaultBorrowingFeeDecayToleranceMinutes
        };
    }
    else if (typeof maxBorrowingRateOrOptionalParams === "number" ||
        typeof maxBorrowingRateOrOptionalParams === "string" ||
        maxBorrowingRateOrOptionalParams instanceof lib_base_1.Decimal) {
        return {
            maxBorrowingRate: lib_base_1.Decimal.from(maxBorrowingRateOrOptionalParams),
            borrowingFeeDecayToleranceMinutes: defaultBorrowingFeeDecayToleranceMinutes
        };
    }
    else {
        const { maxBorrowingRate, borrowingFeeDecayToleranceMinutes } = maxBorrowingRateOrOptionalParams;
        return {
            maxBorrowingRate: maxBorrowingRate !== undefined
                ? lib_base_1.Decimal.from(maxBorrowingRate)
                : (_b = currentBorrowingRate === null || currentBorrowingRate === void 0 ? void 0 : currentBorrowingRate.add(defaultBorrowingRateSlippageTolerance)) !== null && _b !== void 0 ? _b : lib_base_1.Decimal.ZERO,
            borrowingFeeDecayToleranceMinutes: borrowingFeeDecayToleranceMinutes !== null && borrowingFeeDecayToleranceMinutes !== void 0 ? borrowingFeeDecayToleranceMinutes : defaultBorrowingFeeDecayToleranceMinutes
        };
    }
};
/**
 * A transaction that has been prepared for sending.
 *
 * @remarks
 * Returned by {@link PopulatableEthersLiquity} functions.
 *
 * @public
 */
class PopulatedEthersLiquityTransaction {
    /** @internal */
    constructor(rawPopulatedTransaction, connection, parse, gasHeadroom) {
        this.rawPopulatedTransaction = rawPopulatedTransaction;
        this._connection = connection;
        this._parse = parse;
        if (gasHeadroom !== undefined) {
            this.gasHeadroom = gasHeadroom;
        }
    }
    /** {@inheritDoc @liquity/lib-base#PopulatedLiquityTransaction.send} */
    async send() {
        return new SentEthersLiquityTransaction(await EthersLiquityConnection_1._requireSigner(this._connection).sendTransaction(this.rawPopulatedTransaction), this._connection, this._parse);
    }
}
exports.PopulatedEthersLiquityTransaction = PopulatedEthersLiquityTransaction;
/**
 * {@inheritDoc @liquity/lib-base#PopulatedRedemption}
 *
 * @public
 */
class PopulatedEthersRedemption extends PopulatedEthersLiquityTransaction {
    /** @internal */
    constructor(rawPopulatedTransaction, connection, attemptedYUSDAmount, redeemableYUSDAmount, increaseAmountByMinimumNetDebt) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(connection);
        super(rawPopulatedTransaction, connection, 
        //TO DO UPDATE WITH CORRECT ARGS
        ({ logs }) => troveManager
            .extractEvents(logs, "Redemption")
            .map(({ args: { tokens, amounts, YUSDfee, _actualYUSDAmount, _attemptedYUSDAmount } }) => ({
            attemptedYUSDAmount: _utils_1.decimalify(_attemptedYUSDAmount),
            actualYUSDAmount: _utils_1.decimalify(_actualYUSDAmount),
            collateralTaken: _utils_1.decimalify(amounts[0]),
            fee: _utils_1.decimalify(YUSDfee)
        }))[0]);
        this.attemptedYUSDAmount = attemptedYUSDAmount;
        this.redeemableYUSDAmount = redeemableYUSDAmount;
        this.isTruncated = redeemableYUSDAmount.lt(attemptedYUSDAmount);
        this._increaseAmountByMinimumNetDebt = increaseAmountByMinimumNetDebt;
    }
    /** {@inheritDoc @liquity/lib-base#PopulatedRedemption.increaseAmountByMinimumNetDebt} */
    increaseAmountByMinimumNetDebt(maxRedemptionRate) {
        if (!this._increaseAmountByMinimumNetDebt) {
            throw new Error("PopulatedEthersRedemption: increaseAmountByMinimumNetDebt() can " +
                "only be called when amount is truncated");
        }
        return this._increaseAmountByMinimumNetDebt(maxRedemptionRate);
    }
}
exports.PopulatedEthersRedemption = PopulatedEthersRedemption;
/**
 * Ethers-based implementation of {@link @liquity/lib-base#PopulatableLiquity}.
 *
 * @public
 */
class PopulatableEthersLiquity {
    constructor(readable) {
        this._readable = readable;
    }
    _wrapSimpleTransaction(rawPopulatedTransaction) {
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, noDetails);
    }
    _wrapTroveChangeWithFees(params, rawPopulatedTransaction, gasHeadroom) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, 
        //   const [newTrove] = borrowerOperations
        //   .extractEvents(logs, "TroveUpdated")
        //   .map(({ args: { _debt, _tokens, _amounts  } }) => new Trove(convertToMap(_tokens, _amounts), convertToMap(["debt"], [_debt])));
        // const [fee] = borrowerOperations
        //   .extractEvents(logs, "YUSDBorrowingFeePaid")
        //   .map(({ args: { _YUSDFee } }) => decimalify(_YUSDFee));
        // return {
        //   params,
        //   newTrove,
        //   fee
        ({ logs }) => {
            return { params: params, newTrove: new lib_base_1.Trove({}, {}, { "debt": lib_base_1.Decimal.ZERO }), fee: lib_base_1.Decimal.ZERO };
            // };
        }, gasHeadroom);
    }
    async _wrapTroveClosure(rawPopulatedTransaction) {
        const { activePool, yusdToken } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs, from: userAddress }) => {
            const [repayYUSD] = yusdToken
                .extractEvents(logs, "Transfer")
                .filter(({ args: { from, to } }) => from === userAddress && to === constants_1.AddressZero)
                .map(({ args: { value } }) => convertToMap(['debt'], [value]));
            const [withdrawCollaterals] = activePool
                .extractEvents(logs, "CollateralsSent")
                .filter(({ args: { _to } }) => _to === userAddress)
                .map(({ args: { _collaterals, _amounts } }) => convertToMap(_collaterals, _amounts));
            return {
                params: repayYUSD['debt'].nonZero ? { withdrawCollaterals, repayYUSD } : { withdrawCollaterals }
            };
        });
    }
    async _wrapTroveClosureUnleverUp(rawPopulatedTransaction) {
        const { activePool, yusdToken } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs, from: userAddress }) => {
            const [repayYUSD] = yusdToken
                .extractEvents(logs, "Transfer")
                .filter(({ args: { from, to } }) => from === userAddress && to === constants_1.AddressZero)
                .map(({ args: { value } }) => convertToMap(['debt'], [value]));
            const [withdrawCollaterals] = activePool
                .extractEvents(logs, "CollateralsSent")
                .filter(({ args: { _to } }) => _to === userAddress)
                .map(({ args: { _collaterals, _amounts } }) => convertToMap(_collaterals, _amounts));
            const withdrawCollateralsMaxSlippages = {};
            return {
                params: { withdrawCollaterals, withdrawCollateralsMaxSlippages, repayYUSD }
            };
        });
    }
    _wrapLiquidation(rawPopulatedTransaction, decimals) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs }) => {
            const liquidatedAddresses = troveManager
                .extractEvents(logs, "TroveLiquidated")
                .map(({ args: { _borrower } }) => _borrower);
            const [totals] = troveManager
                .extractEvents(logs, "Liquidation")
                .map(({ args: { totalYUSDGasCompensation, totalCollGasCompAmounts, totalCollGasCompTokens, totalCollTokens, totalCollAmounts, liquidatedAmount } }) => ({
                collateralGasCompensation: convertToMap(totalCollGasCompTokens, totalCollGasCompAmounts),
                yusdGasCompensation: _utils_1.decimalify(totalYUSDGasCompensation),
                totalLiquidated: new lib_base_1.Trove(convertToMap(totalCollTokens, totalCollAmounts), decimals, convertToMap(['debt'], [liquidatedAmount]))
            }));
            return {
                liquidatedAddresses,
                ...totals
            };
        });
    }
    _extractStabilityPoolGainsWithdrawalDetails(logs) {
        // const { stabilityPool } = _getContracts(this._readable.connection);
        // const [newYUSDDeposit] = stabilityPool
        //   .extractEvents(logs, "UserDepositChanged")
        //   .map(({ args: { _newDeposit } }) => decimalify(_newDeposit));
        // const [[collateralGain, yusdLoss]] = stabilityPool
        //   .extractEvents(logs, "ETHGainWithdrawn")
        //   .map(({ args: { _ETH, _YUSDLoss } }) => [decimalify(_ETH), decimalify(_YUSDLoss)]);
        // const [yetiReward] = stabilityPool
        //   .extractEvents(logs, "YETIPaidToDepositor")
        //   .map(({ args: { _YETI } }) => decimalify(_YETI));
        return {
            yusdLoss: lib_base_1.Decimal.ZERO,
            newYUSDDeposit: lib_base_1.Decimal.from(100),
            collateralGain: lib_base_1.Decimal.from(0),
            yetiReward: lib_base_1.Decimal.from(0)
        };
    }
    _wrapStabilityPoolGainsWithdrawal(rawPopulatedTransaction) {
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs }) => this._extractStabilityPoolGainsWithdrawalDetails(logs));
    }
    _wrapStabilityDepositTopup(change, rawPopulatedTransaction) {
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs }) => ({
            ...this._extractStabilityPoolGainsWithdrawalDetails(logs),
            change
        }));
    }
    async _wrapStabilityDepositWithdrawal(rawPopulatedTransaction) {
        const { stabilityPool, yusdToken } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs, from: userAddress }) => {
            const gainsWithdrawalDetails = this._extractStabilityPoolGainsWithdrawalDetails(logs);
            const [withdrawYUSD] = yusdToken
                .extractEvents(logs, "Transfer")
                .filter(({ args: { from, to } }) => from === stabilityPool.address && to === userAddress)
                .map(({ args: { value } }) => _utils_1.decimalify(value));
            return {
                ...gainsWithdrawalDetails,
                change: { withdrawYUSD, withdrawAllYUSD: gainsWithdrawalDetails.newYUSDDeposit.isZero }
            };
        });
    }
    _wrapCollateralGainTransfer(rawPopulatedTransaction) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs }) => {
            // const [newTrove] = borrowerOperations
            //   .extractEvents(logs, "TroveUpdated")
            //   .map(({ args: { _tokens, _amounts, _debt } }) => new Trove(convertToMap(_tokens, _amounts), convertToMap(['debt'], [_debt])));
            return {
                ...this._extractStabilityPoolGainsWithdrawalDetails(logs),
                newTrove: new lib_base_1.Trove({}, {}, { "debt": lib_base_1.Decimal.ZERO })
            };
        });
    }
    async _findHintsForNominalCollateralRatio(nominalCollateralRatio) {
        const { sortedTroves, hintHelpers } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const numberOfTroves = await this._readable.getNumberOfTroves();
        if (!numberOfTroves) {
            return [constants_1.AddressZero, constants_1.AddressZero];
        }
        if (nominalCollateralRatio.infinite) {
            return [constants_1.AddressZero, await sortedTroves.getFirst()];
        }
        const totalNumberOfTrials = Math.ceil(4000);
        const [firstTrials, ...restOfTrials] = generateTrials(totalNumberOfTrials);
        const collectApproxHint = ({ latestRandomSeed, results }, numberOfTrials) => hintHelpers
            .getApproxHint(nominalCollateralRatio.hex, numberOfTrials, latestRandomSeed)
            .then(({ latestRandomSeed, ...result }) => ({
            latestRandomSeed,
            results: [...results, result]
        }));
        const { results } = await restOfTrials.reduce((p, numberOfTrials) => p.then(state => collectApproxHint(state, numberOfTrials)), collectApproxHint({ latestRandomSeed: randomInteger(), results: [] }, firstTrials));
        const { hintAddress } = results.reduce((a, b) => (a.diff.lt(b.diff) ? a : b));
        const [prev, next] = await sortedTroves.findInsertPosition(nominalCollateralRatio.hex, hintAddress, hintAddress);
        return prev === constants_1.AddressZero ? [next, next] : next === constants_1.AddressZero ? [prev, prev] : [prev, next];
    }
    async _findHints(trove, ICR) {
        if (trove instanceof lib_base_1.TroveWithPendingRedistribution) {
            throw new Error("Rewards must be applied to this Trove");
        }
        // const collPrices: TroveMappings = {};
        // for (let i = 0; i < whitelistedCollaterals.length; i++) {
        //   const [colPrice] = await Promise.all([this._readable.getCollPrice(whitelistedCollaterals[i])]);
        //   collPrices[whitelistedCollaterals[i]] = colPrice;
        // }
        return this._findHintsForNominalCollateralRatio(lib_base_1.Decimal.from(ICR));
    }
    async _findRedemptionHints(amount) {
        const { hintHelpers } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const price = lib_base_1.Decimal.ZERO; // await this._readable.getCollPrice();
        const { firstRedemptionHint, partialRedemptionHintAICR, truncatedYUSDamount } = await hintHelpers.getRedemptionHints(amount.hex, exports._redeemMaxIterations);
        const [partialRedemptionUpperHint, partialRedemptionLowerHint] = partialRedemptionHintAICR.isZero()
            ? [constants_1.AddressZero, constants_1.AddressZero]
            : await this._findHintsForNominalCollateralRatio(_utils_1.decimalify(partialRedemptionHintAICR));
        return [
            _utils_1.decimalify(truncatedYUSDamount),
            firstRedemptionHint,
            partialRedemptionUpperHint,
            partialRedemptionLowerHint,
            partialRedemptionHintAICR
        ];
    }
    async approveERC20(tokenAddress, toAddress, amount, overrides) {
        const signer = EthersLiquityConnection_1._requireSigner(this._readable.connection);
        const ERC20 = contracts_1._getERC20Token(tokenAddress, signer);
        const result = await ERC20.token.estimateAndPopulate.approve({ ...overrides }, id, toAddress, lib_base_1.Decimal.from(amount).hex);
        return this._wrapSimpleTransaction(result);
    }
    async multipleApproveERC20(tokenAddresses, toAddresses, amounts) {
        assert_1.default(tokenAddresses.length === toAddresses.length, "tokenAddresses and toAddresses need to have equal length");
        assert_1.default(tokenAddresses.length === amounts.length, "tokenAddresses and amounts need to have equal length");
        const signer = EthersLiquityConnection_1._requireSigner(this._readable.connection);
        for (let i = 0; i < tokenAddresses.length; i++) {
            const ERC20 = contracts_1._getERC20Token(tokenAddresses[i], signer);
            await ERC20.token.approve(toAddresses[i], lib_base_1.Decimal.from(amounts[i]).hex);
        }
    }
    async mintERC20(tokenAddress, overrides) {
        const signer = EthersLiquityConnection_1._requireSigner(this._readable.connection);
        const ERC20 = contracts_1._getTestERC20Token(tokenAddress, signer);
        return (await this._wrapSimpleTransaction(await ERC20.token.estimateAndPopulate.mint({ ...overrides }, id)));
    }
    async mintERC20NoLimit(tokenAddress, amount, overrides) {
        const signer = EthersLiquityConnection_1._requireSigner(this._readable.connection);
        const ERC20 = contracts_1._getTestERC20TokenNoLimit(tokenAddress, signer);
        return (await this._wrapSimpleTransaction(await ERC20.token.estimateAndPopulate.mint({ ...overrides }, id, amount.hex)));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.openTrove} */
    async openTrove(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const whitelistedCollaterals = await this._readable.getWhitelistedCollaterals();
        const normalizedParams = lib_base_1._normalizeTroveCreation(params);
        const { depositCollaterals, borrowYUSD } = normalizedParams;
        const [fees, blockTimestamp, total, recoveryRatios] = await Promise.all([
            this._readable._getFeesFactory(),
            this._readable._getBlockTimestamp(),
            this._readable.getTotal(),
            this._readable.getRecoveryRatios(whitelistedCollaterals)
        ]);
        const priceMapping = {};
        for (let i = 0; i < whitelistedCollaterals.length; i++) {
            const [colPrice] = await Promise.all([this._readable.getCollPrice(whitelistedCollaterals[i])]);
            priceMapping[whitelistedCollaterals[i]] = colPrice;
        }
        const recoveryMode = total.collateralRatioIsBelowCritical(priceMapping, recoveryRatios);
        const decayBorrowingRate = (seconds) => fees(blockTimestamp + seconds, recoveryMode).borrowingRate();
        const currentBorrowingRate = decayBorrowingRate(0);
        const newTrove = lib_base_1.Trove.create(normalizedParams, currentBorrowingRate);
        const hints = await this._findHints(newTrove, ICRWithFees);
        const { maxBorrowingRate, borrowingFeeDecayToleranceMinutes } = normalizeBorrowingOperationOptionalParams(maxBorrowingRateOrOptionalParams, currentBorrowingRate);
        const txParams = (borrowYUSD, collateral, collateralAmounts) => [
            maxBorrowingRate.hex,
            borrowYUSD.hex,
            ...hints,
            collateral,
            collateralAmounts,
        ];
        const collateralNames = Object.keys(depositCollaterals);
        collateralNames.sort(function (a, b) {
            return whitelistedCollaterals.indexOf(a) - whitelistedCollaterals.indexOf(b);
        });
        const collateral = [];
        for (let i = 0; i < collateralNames.length; i++) {
            collateral[i] = depositCollaterals[collateralNames[i]].hex;
        }
        let gasHeadroom;
        if ((overrides === null || overrides === void 0 ? void 0 : overrides.gasLimit) === undefined) {
            const decayedBorrowingRate = decayBorrowingRate(60 * borrowingFeeDecayToleranceMinutes);
            const decayedTrove = lib_base_1.Trove.create(normalizedParams, decayedBorrowingRate);
            const { borrowYUSD: borrowYUSDSimulatingDecay } = lib_base_1.Trove.recreate(decayedTrove, currentBorrowingRate);
            if (decayedTrove.debt['debt'].lt(lib_base_1.YUSD_MINIMUM_DEBT)) {
                throw new Error(`Trove's debt might fall below ${lib_base_1.YUSD_MINIMUM_DEBT} ` +
                    `within ${borrowingFeeDecayToleranceMinutes} minutes`);
            }
            // console.log("OPEN TROVE PARAMS", ...txParams(borrowYUSD['debt'], collateralNames, collateral))
            const [gasNow, gasLater] = await Promise.all([
                borrowerOperations.estimateGas.openTrove(...txParams(borrowYUSD['debt'], collateralNames, collateral)),
                borrowerOperations.estimateGas.openTrove(...txParams(borrowYUSDSimulatingDecay['debt'], collateralNames, collateral))
            ]);
            // console.log('gasNow', gasNow)
            // console.log('gasLater', gasLater)
            const gasTotal = addGasForBaseRateUpdate(borrowingFeeDecayToleranceMinutes)(bigNumberMax(addGasForPotentialListTraversal(gasNow), gasLater));
            const gasLimit = Math.min((gasTotal.toNumber() * 2) + 1000000, 7900000);
            gasHeadroom = gasLimit;
            overrides = { ...overrides, gasLimit };
        }
        return this._wrapTroveChangeWithFees(normalizedParams, await borrowerOperations.populateTransaction.openTrove(maxBorrowingRate.hex, borrowYUSD['debt'].hex, ...hints, collateralNames, collateral, overrides), gasHeadroom);
    }
    async openTroveLeverUp(params, ICRWithFees, troveOpen, maxBorrowingRateOrOptionalParams, overrides) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const normalizedParams = lib_base_1._normalizeTroveLeverUpCreation(params);
        const { depositCollaterals, depositCollateralsLeverages, depositCollateralsMaxSlippages, borrowYUSD, decimals } = normalizedParams;
        const postLeverageAmounts = {};
        const depositedCollateralsArray = Object.keys(depositCollaterals);
        for (let i = 0; i < depositedCollateralsArray.length; i++) {
            postLeverageAmounts[depositedCollateralsArray[i]] = depositCollaterals[depositedCollateralsArray[i]].mul(depositCollateralsLeverages[depositedCollateralsArray[i]]);
        }
        // console.log("postLeverageAmounts", postLeverageAmounts)
        const debt = borrowYUSD["debt"];
        const creationParams = {
            depositCollaterals: postLeverageAmounts,
            decimals: decimals,
            borrowYUSD: { "debt": debt }
        };
        // const adjustParams:TroveAdjustmentParams<TroveMappings> = {
        //   depositCollaterals: postLeverageAmounts,
        //   withdrawCollaterals: {},
        //   borrowYUSD: { "debt": debt }
        // };
        const normalizedCreationParams = lib_base_1._normalizeTroveCreation(creationParams);
        // if (troveOpen == "open") {
        //   const normalizedCreationParams = _normalizeTroveCreation(creationParams)
        // } else {
        //   const normalizedCreationParams = _normalizeTroveCreation(creationParams)
        // }
        // console.log(normalizedCreationParams)
        const [fees, blockTimestamp, total] = await Promise.all([
            this._readable._getFeesFactory(),
            this._readable._getBlockTimestamp(),
            this._readable.getTotal()
        ]);
        const priceMapping = {};
        const whitelistedCollaterals = await this._readable.getWhitelistedCollaterals();
        for (let i = 0; i < whitelistedCollaterals.length; i++) {
            const [colPrice] = await Promise.all([this._readable.getCollPrice(whitelistedCollaterals[i])]);
            priceMapping[whitelistedCollaterals[i]] = colPrice;
        }
        const recoveryRatios = await this._readable.getRecoveryRatios(whitelistedCollaterals);
        const recoveryMode = total.collateralRatioIsBelowCritical(priceMapping, recoveryRatios);
        const decayBorrowingRate = (seconds) => fees(blockTimestamp + seconds, recoveryMode).borrowingRate();
        const currentBorrowingRate = decayBorrowingRate(0);
        // const levera_normalized = 
        const newTrove = lib_base_1.Trove.create(normalizedCreationParams, ICRWithFees);
        const hints = await this._findHints(newTrove, ICRWithFees);
        const { maxBorrowingRate, borrowingFeeDecayToleranceMinutes } = normalizeBorrowingOperationOptionalParams(maxBorrowingRateOrOptionalParams, currentBorrowingRate);
        const txParams = (borrowYUSD, collateral, collateralAmounts, leverages, maxSlippages) => [
            maxBorrowingRate.hex,
            borrowYUSD.hex,
            ...hints,
            collateral,
            collateralAmounts,
            leverages,
            maxSlippages,
            { ...overrides }
        ];
        const collateralNames = Object.keys(depositCollaterals);
        collateralNames.sort(function (a, b) {
            return whitelistedCollaterals.indexOf(a) - whitelistedCollaterals.indexOf(b);
        });
        const amount = [];
        const leverages = [];
        const maxSlippages = [];
        for (let i = 0; i < collateralNames.length; i++) {
            amount[i] = depositCollaterals[collateralNames[i]].hex;
            leverages[i] = depositCollateralsLeverages[collateralNames[i]].hex;
            maxSlippages[i] = depositCollateralsMaxSlippages[collateralNames[i]].hex;
        }
        let gasHeadroom;
        if ((overrides === null || overrides === void 0 ? void 0 : overrides.gasLimit) === undefined) {
            const decayedBorrowingRate = decayBorrowingRate(60 * borrowingFeeDecayToleranceMinutes);
            const decayedTrove = lib_base_1.Trove.create(normalizedCreationParams, decayedBorrowingRate);
            const { borrowYUSD: borrowYUSDSimulatingDecay } = lib_base_1.Trove.recreate(decayedTrove, currentBorrowingRate);
            // console.log("txParams(borrowYUSD['debt']", borrowYUSD['debt'])
            // console.log("collaterals", collateralNames)
            // console.log("amountHex", amount)
            // console.log("leveragesHex", leverages)
            // console.log("maxSlippagesHex", maxSlippages)
            const [gasNow, gasLater] = await Promise.all([
                borrowerOperations.estimateGas.openTroveLeverUp(...txParams(borrowYUSD['debt'], collateralNames, amount, leverages, maxSlippages)),
                borrowerOperations.estimateGas.openTroveLeverUp(...txParams(borrowYUSD['debt'], collateralNames, amount, leverages, maxSlippages))
            ]);
            // console.log('gasNow', gasNow)
            // console.log('gasLater', gasLater)
            const gasLimit = addGasForBaseRateUpdate(borrowingFeeDecayToleranceMinutes)(bigNumberMax(addGasForPotentialListTraversal(gasNow), gasLater));
            gasHeadroom = gasLimit.toNumber() + 1000000;
            overrides = { ...overrides, gasLimit };
        }
        return this._wrapTroveChangeWithFees(normalizedParams, await borrowerOperations.populateTransaction.openTroveLeverUp(maxBorrowingRate.hex, borrowYUSD['debt'].hex, ...hints, collateralNames, amount, leverages, maxSlippages, overrides), gasHeadroom);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.closeTrove} */
    async closeTrove(overrides) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapTroveClosure(await borrowerOperations.estimateAndPopulate.closeTrove({ ...overrides }, id));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.closeTroveUnleverUp} */
    async closeTroveUnleverUp(params, overrides) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const whitelistedCollaterals = await this._readable.getWhitelistedCollaterals();
        const { withdrawCollaterals, withdrawCollateralsMaxSlippages } = params;
        const collateralNames = Object.keys(withdrawCollaterals);
        collateralNames.sort(function (a, b) {
            return whitelistedCollaterals.indexOf(a) - whitelistedCollaterals.indexOf(b);
        });
        const amount = [];
        const maxSlippages = [];
        for (let i = 0; i < collateralNames.length; i++) {
            amount[i] = withdrawCollaterals[collateralNames[i]].hex;
            maxSlippages[i] = withdrawCollateralsMaxSlippages[collateralNames[i]].hex;
        }
        console.log('collateralNames', collateralNames);
        console.log('amount', amount);
        console.log('maxSlippages', maxSlippages);
        return this._wrapTroveClosureUnleverUp(await borrowerOperations.estimateAndPopulate.closeTroveUnlever({ ...overrides }, id, collateralNames, amount, maxSlippages));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.depositCollateral} */
    depositCollateral(amount, ICRWithFees, overrides) {
        return this.adjustTrove({ depositCollaterals: amount }, ICRWithFees, undefined, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.withdrawCollateral} */
    withdrawCollateral(amount, ICRWithFees, overrides) {
        return this.adjustTrove({ withdrawCollaterals: amount }, ICRWithFees, undefined, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.borrowYUSD} */
    borrowYUSD(amount, ICRWithFees, maxBorrowingRate, overrides) {
        return this.adjustTrove({ borrowYUSD: amount }, ICRWithFees, maxBorrowingRate, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.repayYUSD} */
    repayYUSD(amount, ICRWithFees, overrides) {
        return this.adjustTrove({ repayYUSD: amount }, ICRWithFees, undefined, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.adjustTrove} */
    async adjustTrove(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides) {
        const whitelistedCollaterals = await this._readable.getWhitelistedCollaterals();
        const recoveryRatios = await this._readable.getRecoveryRatios(whitelistedCollaterals);
        const address = EthersLiquityConnection_1._requireAddress(this._readable.connection, overrides);
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const normalizedParams = lib_base_1._normalizeTroveAdjustment(params);
        const { depositCollaterals, withdrawCollaterals, borrowYUSD, repayYUSD } = normalizedParams;
        // Checks if it is a borrow or a repay adjustment. 
        let borrowIncrease = false;
        let borrowChange = lib_base_1.Decimal.ZERO;
        // console.log("repayYUSD", repayYUSD)
        if (repayYUSD != undefined) {
            borrowChange = repayYUSD["debt"];
        }
        if (borrowYUSD != undefined) {
            // console.log("borrowYUSD adjust", borrowYUSD)
            if (borrowYUSD["debt"] != lib_base_1.Decimal.ZERO) {
                // console.log("borrowYUSD[debt]", borrowYUSD["debt"])
                borrowIncrease = true;
            }
            borrowChange = borrowYUSD["debt"];
        }
        let deposited = {};
        let withdraw = {};
        // Check to see if DepositCollaterals or Withdraw are undefined 
        if (depositCollaterals != undefined) {
            deposited = depositCollaterals;
        }
        if (withdrawCollaterals != undefined) {
            withdraw = withdrawCollaterals;
        }
        // Get the current collateral prices to be adjusted
        const collPrices = {};
        for (let i = 0; i < whitelistedCollaterals.length; i++) {
            const [colPrice] = await Promise.all([this._readable.getCollPrice(whitelistedCollaterals[i])]);
            collPrices[whitelistedCollaterals[i]] = colPrice;
        }
        // for (let i = 0; i < finalWithdraw.length; i++) {
        //   collPrices[finalWithdraw[i]] = await this._readable.getCollPrice(finalWithdraw[i]);
        // }
        // console.log("COL PRICES POPULATABLE", collPrices)
        const collateralNamesDeposit = Object.keys(deposited);
        const collateralNamesWithdraw = Object.keys(withdraw);
        collateralNamesDeposit.sort(function (a, b) {
            return whitelistedCollaterals.indexOf(a) - whitelistedCollaterals.indexOf(b);
        });
        collateralNamesWithdraw.sort(function (a, b) {
            return whitelistedCollaterals.indexOf(a) - whitelistedCollaterals.indexOf(b);
        });
        const amountDeposit = [];
        const amountWithdraw = [];
        for (let i = 0; i < collateralNamesDeposit.length; i++) {
            amountDeposit[i] = deposited[collateralNamesDeposit[i]].hex;
        }
        for (let i = 0; i < collateralNamesWithdraw.length; i++) {
            amountWithdraw[i] = withdraw[collateralNamesWithdraw[i]].hex;
        }
        // Initialize trove and feeVars which are used to find hints. 
        const [trove, feeVars] = await Promise.all([
            this._readable.getTrove(address),
            borrowYUSD &&
                _utils_1.promiseAllValues({
                    fees: this._readable._getFeesFactory(),
                    blockTimestamp: this._readable._getBlockTimestamp(),
                    total: this._readable.getTotal(),
                    price: collPrices
                })
        ]);
        const decayBorrowingRate = (seconds) => feeVars === null || feeVars === void 0 ? void 0 : feeVars.fees(feeVars.blockTimestamp + seconds, feeVars.total.collateralRatioIsBelowCritical(feeVars.price, recoveryRatios)).borrowingRate();
        const currentBorrowingRate = decayBorrowingRate(0);
        // console.log("PopulatableEthersLiquity.ts, normalizedParams", normalizedParams)
        const adjustedTrove = trove.adjust(normalizedParams, currentBorrowingRate);
        // console.log("PopulatableEthersLiquity.ts, ICRwithfees", ICRWithFees)
        const hints = await this._findHints(adjustedTrove, ICRWithFees);
        const { maxBorrowingRate, borrowingFeeDecayToleranceMinutes } = normalizeBorrowingOperationOptionalParams(maxBorrowingRateOrOptionalParams, currentBorrowingRate);
        // Removed value: key in object. 
        const txParams = (borrowYUSD, collateralIn, amountsIn, collateralOut, amountsOut) => [
            collateralIn,
            amountsIn,
            collateralOut,
            amountsOut,
            borrowYUSD.hex,
            borrowIncrease,
            ...hints,
            maxBorrowingRate.hex,
            { ...overrides }
        ];
        let gasHeadroom;
        if ((overrides === null || overrides === void 0 ? void 0 : overrides.gasLimit) === undefined) {
            const decayedBorrowingRate = decayBorrowingRate(60 * borrowingFeeDecayToleranceMinutes);
            const decayedTrove = trove.adjust(normalizedParams, decayedBorrowingRate);
            const { borrowYUSD: borrowYUSDSimulatingDecay } = trove.adjustTo(decayedTrove, currentBorrowingRate);
            // if (decayedTrove.debt['debt'].lt(YUSD_MINIMUM_DEBT)) {
            //   throw new Error(
            //     `Trove's debt might fall below ${YUSD_MINIMUM_DEBT} ` +
            //       `within ${borrowingFeeDecayToleranceMinutes} minutes`
            //   );
            // }
            //}
            let finalReturn = {};
            if (borrowYUSD == undefined) {
                finalReturn = { "debt": lib_base_1.Decimal.ZERO };
            }
            else {
                finalReturn = borrowYUSD;
            }
            // console.log("collateralNamesDeposit, amountDeposit, collateralNamesWithdraw, amountWithdraw, borrowChange.hex, borrowIncrease, ...hints, maxBorrowingRate.hex", collateralNamesDeposit, amountDeposit, collateralNamesWithdraw, amountWithdraw, borrowChange.hex, borrowIncrease, ...hints, maxBorrowingRate.hex)
            const [gasNow, gasLater] = await Promise.all([
                borrowerOperations.estimateGas.adjustTrove(collateralNamesDeposit, amountDeposit, collateralNamesWithdraw, amountWithdraw, borrowChange.hex, borrowIncrease, ...hints, maxBorrowingRate.hex),
                borrowerOperations.estimateGas.adjustTrove(collateralNamesDeposit, amountDeposit, collateralNamesWithdraw, amountWithdraw, borrowChange.hex, borrowIncrease, ...hints, maxBorrowingRate.hex)
            ]);
            const gastotal = bigNumberMax(addGasForPotentialListTraversal(gasNow), gasLater);
            // if (borrowYUSD) {
            //   gasLimit = addGasForBaseRateUpdate(borrowingFeeDecayToleranceMinutes)(gasLimit);
            // }
            const gasLimit = Math.min((gastotal.toNumber() * 2) + 1000000, 7900000);
            gasHeadroom = (gastotal.toNumber() * 2) + 1000000;
            overrides = { ...overrides, gasLimit };
        }
        // console.log("adjust params", collateralNamesDeposit, amountDeposit, collateralNamesWithdraw, amountWithdraw, borrowChange.hex, borrowIncrease, ...hints, maxBorrowingRate.hex)
        return this._wrapTroveChangeWithFees(normalizedParams, await borrowerOperations.populateTransaction.adjustTrove(collateralNamesDeposit, amountDeposit, collateralNamesWithdraw, amountWithdraw, borrowChange.hex, borrowIncrease, ...hints, maxBorrowingRate.hex, overrides), gasHeadroom);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.addCollLeverUp} */
    async addCollLeverUp(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides) {
        const address = EthersLiquityConnection_1._requireAddress(this._readable.connection, overrides);
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const whitelistedCollaterals = await this._readable.getWhitelistedCollaterals();
        console.log("1");
        const getRecoveryRatios = await this._readable.getRecoveryRatios(whitelistedCollaterals);
        console.log("2");
        const normalizedParams = lib_base_1._normalizeTroveAddCollLeverUp(params);
        console.log("3");
        const { depositCollaterals, depositCollateralsLeverages, depositCollateralsMaxSlippages, borrowYUSD } = normalizedParams;
        console.log("4", depositCollateralsMaxSlippages);
        // Checks if it is a borrow or a repay adjustment. 
        let borrowIncrease = false;
        let borrowChange = lib_base_1.Decimal.ZERO;
        if (borrowYUSD != undefined) {
            // console.log("borrowYUSD adjust", borrowYUSD)
            if (borrowYUSD["debt"] != lib_base_1.Decimal.ZERO) {
                // console.log("borrowYUSD[debt]", borrowYUSD["debt"])
                borrowIncrease = true;
            }
            borrowChange = borrowYUSD["debt"];
        }
        let deposited = {};
        // Check to see if DepositCollaterals is undefined 
        if (depositCollaterals != undefined) {
            deposited = depositCollaterals;
        }
        console.log("5");
        // Get the current collateral prices to be adjusted
        const collPrices = {};
        for (let i = 0; i < whitelistedCollaterals.length; i++) {
            const [colPrice] = await Promise.all([this._readable.getCollPrice(whitelistedCollaterals[i])]);
            collPrices[whitelistedCollaterals[i]] = colPrice;
        }
        console.log("6");
        const collateralNames = Object.keys(deposited);
        collateralNames.sort(function (a, b) {
            return whitelistedCollaterals.indexOf(a) - whitelistedCollaterals.indexOf(b);
        });
        console.log("7");
        const amount = [];
        const leverages = [];
        const maxSlippages = [];
        for (let i = 0; i < collateralNames.length; i++) {
            amount[i] = depositCollaterals[collateralNames[i]].hex;
            leverages[i] = depositCollateralsLeverages[collateralNames[i]].hex;
            maxSlippages[i] = depositCollateralsMaxSlippages[collateralNames[i]].hex;
        }
        console.log("8");
        // Initialize trove and feeVars which are used to find hints. 
        const [trove, feeVars] = await Promise.all([
            this._readable.getTrove(address),
            borrowYUSD &&
                _utils_1.promiseAllValues({
                    fees: this._readable._getFeesFactory(),
                    blockTimestamp: this._readable._getBlockTimestamp(),
                    total: this._readable.getTotal(),
                    price: collPrices
                })
        ]);
        console.log("9");
        const decayBorrowingRate = (seconds) => feeVars === null || feeVars === void 0 ? void 0 : feeVars.fees(feeVars.blockTimestamp + seconds, feeVars.total.collateralRatioIsBelowCritical(feeVars.price, getRecoveryRatios)).borrowingRate();
        console.log("10");
        const currentBorrowingRate = decayBorrowingRate(0);
        const tempTroveAdjustmentParams = normalizedParams.borrowYUSD
            ? {
                depositCollaterals: normalizedParams.depositCollaterals,
                withdrawCollaterals: {},
                borrowYUSD: normalizedParams.borrowYUSD
            }
            : {
                depositCollaterals: normalizedParams.depositCollaterals,
                withdrawCollaterals: {},
            };
        const adjustedTrove = trove.adjust(tempTroveAdjustmentParams, currentBorrowingRate);
        console.log("111");
        const hints = await this._findHints(adjustedTrove, ICRWithFees);
        console.log("11");
        const { maxBorrowingRate, borrowingFeeDecayToleranceMinutes } = normalizeBorrowingOperationOptionalParams(maxBorrowingRateOrOptionalParams, currentBorrowingRate);
        // // Removed value: key in object. 
        // const txParams = (borrowYUSD: Decimal, collateralIn: string[], amountsIn: string[], collateralOut:string[], amountsOut:string[]): Parameters<typeof borrowerOperations.adjustTrove> => [
        //   collateralIn,
        //   amountsIn,
        //   collateralOut,
        //   amountsOut,
        //   borrowYUSD.hex,
        //   borrowIncrease,
        //   ...hints,
        //   maxBorrowingRate.hex,
        //   { ...overrides }
        // ];
        let gasHeadroom;
        if ((overrides === null || overrides === void 0 ? void 0 : overrides.gasLimit) === undefined) {
            //   const decayedBorrowingRate = decayBorrowingRate(60 * borrowingFeeDecayToleranceMinutes);
            //   const decayedTrove = trove.adjust(normalizedParams, decayedBorrowingRate);
            //   const { borrowYUSD: borrowYUSDSimulatingDecay } = trove.adjustTo(
            //     decayedTrove,
            //     currentBorrowingRate
            //   );
            //   // if (decayedTrove.debt['debt'].lt(YUSD_MINIMUM_DEBT)) {
            //   //   throw new Error(
            //   //     `Trove's debt might fall below ${YUSD_MINIMUM_DEBT} ` +
            //   //       `within ${borrowingFeeDecayToleranceMinutes} minutes`
            //   //   );
            //   // }
            // //}
            //   const [gasNow, gasLater] = await Promise.all([
            //     borrowerOperations.estimateGas.adjustTrove(...txParams(borrowChange, finalDeposit, new_collateral_hex, finalWithdraw, new_collateral_hex_withdraw)),
            //     borrowYUSD && borrowYUSDSimulatingDecay &&
            //       borrowerOperations.estimateGas.adjustTrove(...txParams(borrowYUSDSimulatingDecay['debt'], finalDeposit, new_collateral_hex, finalWithdraw, new_collateral_hex_withdraw))
            //   ]);
            //   let gasLimit = bigNumberMax(addGasForPotentialListTraversal(gasNow), gasLater);
            //   if (borrowYUSD) {
            //     gasLimit = addGasForBaseRateUpdate(borrowingFeeDecayToleranceMinutes)(gasLimit);
            //   }
            // gasHeadroom = gasLimit.sub(gasNow).toNumber();
        }
        let finalReturn = {};
        if (borrowYUSD == undefined) {
            finalReturn = { "debt": lib_base_1.Decimal.ZERO };
        }
        else {
            finalReturn = borrowYUSD;
        }
        // const gasNow = await borrowerOperations.estimateGas.addCollLeverUp(collateralNames, amount, leverages, maxSlippages, borrowChange.hex, ...hints, maxBorrowingRate.hex)
        // const gasLimit = gasNow.toNumber() + 1000000
        // overrides = { ...overrides, gasLimit };
        console.log("HELLOW", collateralNames, amount, leverages, maxSlippages, borrowChange.hex, maxBorrowingRate.hex);
        return this._wrapTroveChangeWithFees(normalizedParams, await borrowerOperations.populateTransaction.addCollLeverUp(collateralNames, amount, leverages, maxSlippages, borrowChange.hex, ...hints, maxBorrowingRate.hex, overrides), gasHeadroom);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.addCollLeverUp} */
    async withdrawCollUnleverUp(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides) {
        const whitelistedCollaterals = await this._readable.getWhitelistedCollaterals();
        const recoveryRatios = await this._readable.getRecoveryRatios(whitelistedCollaterals);
        const address = EthersLiquityConnection_1._requireAddress(this._readable.connection, overrides);
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const normalizedParams = lib_base_1._normalizeTroveWithdrawCollUnleverUp(params);
        const { withdrawCollaterals, withdrawCollateralsMaxSlippages, repayYUSD } = normalizedParams;
        let borrowDecrease = false;
        let borrowChange = lib_base_1.Decimal.ZERO;
        if (repayYUSD != undefined) {
            // console.log("borrowYUSD adjust", borrowYUSD)
            if (repayYUSD["debt"] != lib_base_1.Decimal.ZERO) {
                // console.log("borrowYUSD[debt]", borrowYUSD["debt"])
                borrowDecrease = true;
            }
            borrowChange = repayYUSD["debt"];
        }
        let withdrawed = {};
        // Check to see if DepositCollaterals is undefined 
        if (withdrawCollaterals != undefined) {
            withdrawed = withdrawCollaterals;
        }
        // Get the current collateral prices to be adjusted
        const collPrices = {};
        for (let i = 0; i < whitelistedCollaterals.length; i++) {
            const [colPrice] = await Promise.all([this._readable.getCollPrice(whitelistedCollaterals[i])]);
            collPrices[whitelistedCollaterals[i]] = colPrice;
        }
        const collateralNames = Object.keys(withdrawed);
        collateralNames.sort(function (a, b) {
            return whitelistedCollaterals.indexOf(a) - whitelistedCollaterals.indexOf(b);
        });
        const amount = [];
        const maxSlippages = [];
        for (let i = 0; i < collateralNames.length; i++) {
            amount[i] = withdrawed[collateralNames[i]].hex;
            maxSlippages[i] = withdrawCollateralsMaxSlippages[collateralNames[i]].hex;
        }
        // Initialize trove and feeVars which are used to find hints. 
        const [trove, feeVars] = await Promise.all([
            this._readable.getTrove(address),
            repayYUSD &&
                _utils_1.promiseAllValues({
                    fees: this._readable._getFeesFactory(),
                    blockTimestamp: this._readable._getBlockTimestamp(),
                    total: this._readable.getTotal(),
                    price: collPrices
                })
        ]);
        const decayBorrowingRate = (seconds) => feeVars === null || feeVars === void 0 ? void 0 : feeVars.fees(feeVars.blockTimestamp + seconds, feeVars.total.collateralRatioIsBelowCritical(feeVars.price, recoveryRatios)).borrowingRate();
        const currentBorrowingRate = decayBorrowingRate(0);
        const collateralAmountAfterSlippage = {};
        // for (const [key, value] of Object.entries(normalizedParams.withdrawCollaterals)) {
        //   collateralAmountAfterSlippage[key] = value.sub(value.mul(normalizedParams.withdrawCollateralsMaxSlippages[key.concat('slippage')]))
        // }
        //TODO: include slippage 
        const tempTroveAdjustmentParams = normalizedParams.repayYUSD
            ? {
                depositCollaterals: {},
                withdrawCollaterals: normalizedParams.withdrawCollaterals,
                repayYUSD: normalizedParams.repayYUSD
            }
            : {
                depositCollaterals: {},
                withdrawCollaterals: normalizedParams.withdrawCollaterals,
            };
        const adjustedTrove = trove.adjust(tempTroveAdjustmentParams, currentBorrowingRate);
        const hints = await this._findHints(adjustedTrove, ICRWithFees);
        const { maxBorrowingRate, borrowingFeeDecayToleranceMinutes } = normalizeBorrowingOperationOptionalParams(maxBorrowingRateOrOptionalParams, currentBorrowingRate);
        // // Removed value: key in object. 
        // const txParams = (borrowYUSD: Decimal, collateralIn: string[], amountsIn: string[], collateralOut:string[], amountsOut:string[]): Parameters<typeof borrowerOperations.adjustTrove> => [
        //   collateralIn,
        //   amountsIn,
        //   collateralOut,
        //   amountsOut,
        //   borrowYUSD.hex,
        //   borrowIncrease,
        //   ...hints,
        //   maxBorrowingRate.hex,
        //   { ...overrides }
        // ];
        let gasHeadroom;
        // if (overrides?.gasLimit === undefined) {
        //   const decayedBorrowingRate = decayBorrowingRate(60 * borrowingFeeDecayToleranceMinutes);
        //   const decayedTrove = trove.adjust(normalizedParams, decayedBorrowingRate);
        //   const { borrowYUSD: borrowYUSDSimulatingDecay } = trove.adjustTo(
        //     decayedTrove,
        //     currentBorrowingRate
        //   );
        //   // if (decayedTrove.debt['debt'].lt(YUSD_MINIMUM_DEBT)) {
        //   //   throw new Error(
        //   //     `Trove's debt might fall below ${YUSD_MINIMUM_DEBT} ` +
        //   //       `within ${borrowingFeeDecayToleranceMinutes} minutes`
        //   //   );
        //   // }
        // //}
        //   const [gasNow, gasLater] = await Promise.all([
        //     borrowerOperations.estimateGas.adjustTrove(...txParams(borrowChange, finalDeposit, new_collateral_hex, finalWithdraw, new_collateral_hex_withdraw)),
        //     borrowYUSD && borrowYUSDSimulatingDecay &&
        //       borrowerOperations.estimateGas.adjustTrove(...txParams(borrowYUSDSimulatingDecay['debt'], finalDeposit, new_collateral_hex, finalWithdraw, new_collateral_hex_withdraw))
        //   ]);
        //   let gasLimit = bigNumberMax(addGasForPotentialListTraversal(gasNow), gasLater);
        //   if (borrowYUSD) {
        //     gasLimit = addGasForBaseRateUpdate(borrowingFeeDecayToleranceMinutes)(gasLimit);
        //   }
        //   gasHeadroom = gasLimit.sub(gasNow).toNumber();
        //   overrides = { ...overrides, gasLimit };
        // }
        let finalReturn = {};
        if (repayYUSD == undefined) {
            finalReturn = { "debt": lib_base_1.Decimal.ZERO };
        }
        else {
            finalReturn = repayYUSD;
        }
        const gasNow = await borrowerOperations.estimateGas.withdrawCollUnleverUp(collateralNames, amount, maxSlippages, borrowChange.hex, ...hints);
        const gasLimit = gasNow.toNumber() + 1000000;
        overrides = { ...overrides, gasLimit };
        return this._wrapTroveChangeWithFees(normalizedParams, await borrowerOperations.populateTransaction.withdrawCollUnleverUp(collateralNames, amount, maxSlippages, borrowChange.hex, ...hints, overrides), gasHeadroom);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.claimCollateralSurplus} */
    async claimCollateralSurplus(overrides) {
        const { collSurplusPool } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await collSurplusPool.estimateAndPopulate.claimCollateral({ ...overrides }, id));
    }
    /** @internal */
    async setPrice(price, overrides) {
        const { priceFeed } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        if (!contracts_1._priceFeedIsTestnet(priceFeed)) {
            throw new Error("setPrice() unavailable on this deployment of Liquity");
        }
        return this._wrapSimpleTransaction(await priceFeed.estimateAndPopulate.setPrice({ ...overrides }, id, lib_base_1.Decimal.from(price).hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.liquidate} */
    async liquidate(address, liquidator, overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const decimals = await this._readable.getDecimals(await this._readable.getWhitelistedCollaterals());
        if (Array.isArray(address)) {
            return this._wrapLiquidation(await troveManager.estimateAndPopulate.batchLiquidateTroves({ ...overrides }, addGasForYETIIssuance, address, liquidator), decimals);
        }
        else {
            return this._wrapLiquidation(await troveManager.estimateAndPopulate.liquidate({ ...overrides }, addGasForYETIIssuance, address), decimals);
        }
    }
    // /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.liquidateUpTo} */
    // async liquidateUpTo(
    //   maximumNumberOfTrovesToLiquidate: number,
    //   overrides?: EthersTransactionOverrides
    // ): Promise<PopulatedEthersLiquityTransaction<LiquidationDetails>> {
    //   const { troveManager } = _getContracts(this._readable.connection);
    //   return this._wrapLiquidation(
    //     await troveManager.estimateAndPopulate.liquidateTroves(
    //       { ...overrides },
    //       addGasForYETIIssuance,
    //       maximumNumberOfTrovesToLiquidate
    //     )
    //   );
    // }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.depositYUSDInStabilityPool} */
    async depositYUSDInStabilityPool(amount, overrides) {
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const depositYUSD = lib_base_1.Decimal.from(amount);
        return this._wrapStabilityDepositTopup({ depositYUSD }, await stabilityPool.estimateAndPopulate.provideToSP({ ...overrides }, addGasForYETIIssuance, depositYUSD.hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.withdrawYUSDFromStabilityPool} */
    async withdrawYUSDFromStabilityPool(amount, overrides) {
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapStabilityDepositWithdrawal(await stabilityPool.estimateAndPopulate.withdrawFromSP({ ...overrides }, gasLimit, lib_base_1.Decimal.from(amount).hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.withdrawGainsFromStabilityPool} */
    async withdrawGainsFromStabilityPool(overrides) {
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapStabilityPoolGainsWithdrawal(await stabilityPool.estimateAndPopulate.withdrawFromSP({ ...overrides }, gasLimit, lib_base_1.Decimal.ZERO.hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.withdrawYUSDFromStabilityPool} */
    async claimRewardsSwap(amount, swapAssets, reinvest, overrides) {
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await stabilityPool.estimateAndPopulate.claimRewardsSwap({ ...overrides }, id, lib_base_1.Decimal.from(amount).hex, swapAssets, reinvest));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.stakeLPTokens} */
    async stakeLPTokens(amount, overrides) {
        const { boostedFarm } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const depositLPTokens = lib_base_1.Decimal.from(amount);
        return this._wrapSimpleTransaction(await boostedFarm.estimateAndPopulate.deposit({ ...overrides }, id, depositLPTokens.hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.withdrawLPTokens} */
    async withdrawLPTokens(amount, overrides) {
        const { boostedFarm } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const withdrawLPTokens = lib_base_1.Decimal.from(amount);
        return this._wrapSimpleTransaction(await boostedFarm.estimateAndPopulate.withdraw({ ...overrides }, id, withdrawLPTokens.hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.stakeLPTokens} */
    async stakeLPTokensOldFarm(amount, overrides) {
        const { farm } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const depositLPTokens = lib_base_1.Decimal.from(amount);
        return this._wrapSimpleTransaction(await farm.estimateAndPopulate.stake({ ...overrides }, id, depositLPTokens.hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.withdrawLPTokens} */
    async withdrawLPTokensOldFarm(amount, overrides) {
        const { farm } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const withdrawLPTokens = lib_base_1.Decimal.from(amount);
        return this._wrapSimpleTransaction(await farm.estimateAndPopulate.withdraw({ ...overrides }, id, withdrawLPTokens.hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.getFarmRewards} */
    async getFarmRewards(overrides) {
        const { boostedFarm } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await boostedFarm.estimateAndPopulate.withdraw({ ...overrides }, id, lib_base_1.Decimal.from(0).hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.getFarmRewards} */
    async getOldFarmRewards(overrides) {
        const { farm } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await farm.estimateAndPopulate.getReward({ ...overrides }, id));
    }
    async getVeYetiStakeReward(overrides) {
        const { veYETIEmissions } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await veYETIEmissions.estimateAndPopulate.getReward({ ...overrides }, id));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.getFarmRewards} */
    async updateVEYETI(params, overrides) {
        const { veYETI } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await veYETI.estimateAndPopulate.update({ ...overrides }, id, params));
    }
    async notifyAllRewarders(overrides) {
        const { veYETI } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await veYETI.estimateAndPopulate.notifyAllRewarders({ ...overrides }, id));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.transferCollateralGainToTrove} */
    async transferCollateralGainToTrove(overrides) {
        const address = EthersLiquityConnection_1._requireAddress(this._readable.connection, overrides);
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const [initialTrove, stabilityDeposit] = await Promise.all([
            this._readable.getTrove(address),
            this._readable.getStabilityDeposit(address)
        ]);
        const finalTrove = initialTrove.addCollateral(stabilityDeposit.collateralGain);
        return this._wrapCollateralGainTransfer(await stabilityPool.estimateAndPopulate.receiveCollateral({ ...overrides }, compose(addGasForPotentialListTraversal, addGasForYETIIssuance), Object.keys(stabilityDeposit.collateralGain), decToBN(Object.values(stabilityDeposit.collateralGain))));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.sendYUSD} */
    async sendYUSD(toAddress, amount, overrides) {
        const { yusdToken } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await yusdToken.estimateAndPopulate.transfer({ ...overrides }, id, toAddress, lib_base_1.Decimal.from(amount).hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.sendYETI} */
    async sendYETI(toAddress, amount, overrides) {
        const { yetiToken } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await yetiToken.estimateAndPopulate.transfer({ ...overrides }, id, toAddress, lib_base_1.Decimal.from(amount).hex));
    }
    async getMaxRedeemFee(amount) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this._readable.connection);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.redeemYUSD} */
    async redeemYUSD(amount, maxRedemptionRate, overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        //const caller = _requireAddress(this._readable.connection, overrides)
        const attemptedYUSDAmount = lib_base_1.Decimal.from(amount);
        const [fees, total, [truncatedAmount, firstRedemptionHint, ...partialHints]] = await Promise.all([
            this._readable.getFees(),
            this._readable.getTotal(),
            this._findRedemptionHints(attemptedYUSDAmount)
        ]);
        if (truncatedAmount.isZero) {
            throw new Error(`redeemYUSD: amount too low to redeem (try at least ${lib_base_1.YUSD_MINIMUM_NET_DEBT})`);
        }
        const defaultMaxRedemptionRate = (amount) => lib_base_1.Decimal.min(fees.redemptionRate(amount.div(total.debt['debt'])).add(defaultRedemptionRateSlippageTolerance), lib_base_1.Decimal.ONE);
        const populateRedemption = async (attemptedYUSDAmount, maxRedemptionRate, truncatedAmount = attemptedYUSDAmount, partialHints = [constants_1.AddressZero, constants_1.AddressZero, 0]) => {
            const maxRedemptionRateOrDefault = maxRedemptionRate !== undefined
                ? lib_base_1.Decimal.from(maxRedemptionRate)
                : defaultMaxRedemptionRate(truncatedAmount);
            // console.log("YUSD Amount: ", truncatedAmount.hex)
            // console.log("Fee: ", maxRedemptionRateOrDefault.mul(truncatedAmount).hex)
            // console.log("First RedemptionHint: ", firstRedemptionHint)
            // console.log("ParticalHints", ...partialHints)
            return new PopulatedEthersRedemption(await troveManager.estimateAndPopulate.redeemCollateral({ ...overrides }, addGasForBaseRateUpdate(), truncatedAmount.hex, 
            //INSERT MAX FEE UPDATE BELOW,
            maxRedemptionRateOrDefault.mul(truncatedAmount).hex, firstRedemptionHint, ...partialHints, exports._redeemMaxIterations), this._readable.connection, attemptedYUSDAmount, truncatedAmount, truncatedAmount.lt(attemptedYUSDAmount)
                ? newMaxRedemptionRate => populateRedemption(truncatedAmount.add(lib_base_1.YUSD_MINIMUM_NET_DEBT), newMaxRedemptionRate !== null && newMaxRedemptionRate !== void 0 ? newMaxRedemptionRate : maxRedemptionRate)
                : undefined);
        };
        return populateRedemption(attemptedYUSDAmount, maxRedemptionRate, truncatedAmount, partialHints);
    }
}
exports.PopulatableEthersLiquity = PopulatableEthersLiquity;
//# sourceMappingURL=PopulatableEthersLiquity.js.map